import { useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { useTranslationApp } from "../../lib/i18next";
import { BORDER_RADIUS } from "../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { RADIUS } from "../../i18n/keysTranslations";
import { MAPS_RADIUS } from "../../utils/constants";
import TextField from "../Form/TextField";

const round2 = (n) => Math.round(Number(n) * 100) / 100;

const FIELD_NAME = "radiusKm";

const RangeTooltip = ({ defaultRange = 25000, onChange, open, onClose }) => {
  const rangeKm = round2((defaultRange || MAPS_RADIUS.MIN_M) / 1000);
  const clampedKm = Math.max(
    MAPS_RADIUS.MIN_KM,
    Math.min(MAPS_RADIUS.MAX_KM, rangeKm)
  );

  const { t } = useTranslationApp();
  const {
    control,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: { [FIELD_NAME]: clampedKm },
    mode: "onChange",
  });

  const formRadiusKm = watch(FIELD_NAME);

  useEffect(() => {
    if (open) {
      const rangeM = defaultRange || MAPS_RADIUS.MIN_M;
      const rangeKmVal = round2(rangeM / 1000);
      const newRangeKm = Math.max(
        MAPS_RADIUS.MIN_KM,
        Math.min(MAPS_RADIUS.MAX_KM, rangeKmVal)
      );
      const clampedM = round2(newRangeKm * 1000);
      reset({ [FIELD_NAME]: newRangeKm });
      if (rangeM < MAPS_RADIUS.MIN_M || rangeM > MAPS_RADIUS.MAX_M) {
        onChange?.(clampedM);
      }
    }
  }, [open, defaultRange, onChange, reset]);

  const onSliderChange = (_, newValue) => {
    const val = round2(
      Math.max(
        MAPS_RADIUS.MIN_KM,
        Math.min(MAPS_RADIUS.MAX_KM, Number(newValue) || MAPS_RADIUS.MIN_KM)
      )
    );
    setValue(FIELD_NAME, val);
    onChange?.(val * 1000);
  };

  const onInputBlur = useCallback(() => {
    const raw = getValues(FIELD_NAME);
    const parsed = parseFloat(String(raw), 10);
    if (isNaN(parsed) || parsed < MAPS_RADIUS.MIN_KM) {
      setValue(FIELD_NAME, MAPS_RADIUS.MIN_KM);
      onChange?.(MAPS_RADIUS.MIN_M);
    } else if (parsed > MAPS_RADIUS.MAX_KM) {
      setValue(FIELD_NAME, MAPS_RADIUS.MAX_KM);
      onChange?.(MAPS_RADIUS.MAX_M);
    } else {
      const rounded = round2(parsed);
      setValue(FIELD_NAME, rounded);
      onChange?.(rounded * 1000);
    }
  }, [getValues, setValue, onChange]);

  const actionsChange = useCallback(
    (val) => {
      if (val === "" || val === "-") return;
      const raw = String(val);
      const hasMoreThanTwoDecimals = /^\d*\.\d{3,}$/.test(raw);
      if (hasMoreThanTwoDecimals) {
        const truncated = raw.slice(0, raw.indexOf(".") + 3);
        setValue(FIELD_NAME, parseFloat(truncated, 10));
        return;
      }
      const parsed = parseFloat(raw, 10);
      if (!isNaN(parsed)) {
        const clamped = round2(
          Math.max(MAPS_RADIUS.MIN_KM, Math.min(MAPS_RADIUS.MAX_KM, parsed))
        );
        onChange?.(clamped * 1000);
        if (clamped !== parsed) setValue(FIELD_NAME, clamped);
      }
    },
    [setValue, onChange]
  );

  if (!open) return null;

  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        minWidth: 220,
        borderRadius: BORDER_RADIUS,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        backgroundColor: "fields.background",
        position: "relative",
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
        }}
        aria-label="close"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontSize: 11,
          mb: 2,
          display: "block",
          pr: 3,
        }}
      >
        {t(RADIUS)}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: 11,
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {MAPS_RADIUS.MIN_KM} km
        </Typography>
        <Slider
          value={Number(formRadiusKm) || MAPS_RADIUS.MIN_KM}
          min={MAPS_RADIUS.MIN_KM}
          max={MAPS_RADIUS.MAX_KM}
          step={0.01}
          onChange={onSliderChange}
          sx={{
            flex: 1,
            minWidth: 54,
            color: "primary.main",
            height: 8,
            px: 0.5,
            "& .MuiSlider-thumb": {
              width: 18,
              height: 18,
              backgroundColor: "background.paper",
              border: "2px solid",
              borderColor: "primary.main",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0 0 0 6px rgba(25,118,210,0.16)",
              },
            },
            "& .MuiSlider-track": {
              height: 4,
              borderRadius: 2,
            },
            "& .MuiSlider-rail": {
              height: 4,
              borderRadius: 2,
              opacity: 0.4,
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: 11,
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {MAPS_RADIUS.MAX_KM} km
        </Typography>
        <TextField
          control={control}
          name={FIELD_NAME}
          rules={{ required: false }}
          type="number"
          size="small"
          label=""
          variant="filled"
          errors={errors}
          formValues={{ [FIELD_NAME]: formRadiusKm }}
          onBlur={onInputBlur}
          actionsChange={actionsChange}
          inputProps={{
            min: MAPS_RADIUS.MIN_KM,
            max: MAPS_RADIUS.MAX_KM,
            style: {
              width: 36,
              textAlign: "center",
              padding: "6px 4px",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0, mt: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  km
                </Typography>
              </InputAdornment>
            ),
            sx: {
              fontSize: 14,
              fontWeight: 500,
              pr: 0.5,
            },
          }}
          sx={{
            ".MuiInputBase-root": {
              height: 40,
              fontSize: "0.8rem",
            },
            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
              {
                WebkitAppearance: "none",
                margin: 0,
              },
          }}
        />
      </Box>
    </Box>
  );
};

export default RangeTooltip;
